/* eslint-disable */
// NotificationHost — the global card stack. ZERO PROPS.
//
// Precedent: ActivityDrawer takes no user prop either — a system surface scopes
// itself server-side and lets RLS answer "whose rows are these". Nothing is
// threaded through App.js, which is what keeps the App.js edit net-zero.
//
// ⚠️ Renders through createPortal into document.body, and the portal's own root
// element carries .notifRoot. That is not decoration: a portalled node is a
// sibling of the app root, so tokens scoped to a portal class never reach it
// and every var() silently resolves to nothing. See the header of
// notifications.module.css.

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import * as Sentry from '@sentry/react';
import { supabase } from '../../supabase';
import { useNotifications } from '../../hooks/useNotifications';
import { useNotificationChannel } from '../../hooks/useNotificationChannel';
import { NotificationCard } from './NotificationCard';
import styles from './notifications.module.css';
import { useShellStore } from '../shell/shellStore';
import { useUiStore } from '../../stores/uiStore';

const MAX_VISIBLE = 2;

export function NotificationHost() {
  // Shell v3 (S4): the corner cards step aside while a shell panel or the
  // drawer is open — both sit on the same right edge; the cards come back as
  // soon as it closes (nothing is dismissed by this).
  const shellOpen = useShellStore((s) => s.open);
  const drawerOpen = useUiStore((s) => s.showHamburgerDrawer);
  const yieldCards = !!shellOpen || !!drawerOpen;
  const [userId, setUserId] = useState(null);
  const { cards, senderNames, markRead, markAllRead, refetch } = useNotifications();

  // In-handler session read — the established pattern for a system surface that
  // needs an id without prop threading (BasedOnPickers.js:139 documents it).
  // AUD-05 leftover: the LOCAL session (getSession), not getUser() — getUser()
  // is a network round trip to the auth server on every mount, i.e. every
  // screen change. The id is all this needs; RLS still answers the reads.
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession()
      .then(({ data }) => { if (!cancelled) setUserId(data?.session?.user?.id || null); })
      .catch((err) => Sentry.captureException(err, { tags: { source: 'NotificationHost.getSession' } }));
    return () => { cancelled = true; };
  }, []);

  useNotificationChannel({ userId, onMessage: refetch, enabled: !!userId });

  // Renders nothing at all when there is nothing to show. Not an empty div,
  // not a zero — nothing. The negative case in §8.3 step 11 is this line.
  if (!cards.length) return null;

  // Newest at the bottom: the query returns newest-first, so the visible slice
  // is taken from the front and then reversed for painting.
  const visible = cards.slice(0, MAX_VISIBLE).reverse();
  const overflow = cards.length - visible.length;

  return createPortal(
    <div className={styles.notifRoot} data-test="notification-host" data-yield={yieldCards ? '1' : undefined}>
      {cards.length > 1 && (
        <div className={styles.groupBar} data-test="notification-groupbar">
          <span className={styles.groupBarText}>
            {cards.length} announcements waiting for you
          </span>
          <button
            type="button"
            className={styles.groupBarBtn}
            onClick={() => markAllRead()}
            data-test="notification-mark-all"
          >
            Mark all read
          </button>
        </div>
      )}

      {/* Above the cards, not below: newest paints at the bottom, so the ones
          this pill stands in for are the older ones further up.
          Inert in P2a — the drawer that would open is P4. */}
      {overflow > 0 && (
        <div className={styles.morePill} data-test="notification-more">
          +{overflow} more
        </div>
      )}

      {visible.map((row) => (
        <NotificationCard
          key={row.id}
          row={row}
          senderName={senderNames[row.notifications.created_by]}
          onDismiss={markRead}
        />
      ))}
    </div>,
    document.body,
  );
}

export default NotificationHost;
